import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "./AuthContext";

const SubscriptionContext = createContext({
  status: "trial",
  plan: "trial",
  blockedModules: [],
  trialDaysLeft: null,
  allowVideo: false,
  isBlocked: () => false,
  isSuspended: false,
  modules: [],
  hasModuleAccess: () => true,
});

// Map routes to module names for blocking
const ROUTE_TO_MODULE = {
  "/financiero": "financiero",
  "/ai-assistant": "ai_assistant",
  "/ai-insights": "ai_assistant",
  "/intelligence": "intelligence",
  "/marketing": "marketing_automation",
  "/combos": "combos",
  "/reportes": "advanced_reports",
  "/reports": "advanced_reports",
  "/franchise": "franchise",
  "/stock": "inventory",
  "/ponto": "time_clock",
  "/team": "time_clock",
};

export const SubscriptionProvider = ({ children }) => {
  const { authAxios, user } = useAuth();
  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;
  const [state, setState] = useState({
    status: "trial",
    plan: "trial",
    blockedModules: [],
    trialDaysLeft: null,
    allowVideo: false,
  });
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchAll = useCallback(async () => {
    if (!authAxiosRef.current) return;
    setLoading(true);
    try {
      const [subRes, modulesRes] = await Promise.all([
        authAxiosRef.current.get("/subscription/check-access").catch(() => ({ data: null })),
        authAxiosRef.current.get("/marketplace/modules").catch(() => ({ data: { modules: [] } })),
      ]);

      if (subRes.data) {
        setState({
          status: subRes.data?.subscription_status || "trial",
          plan: subRes.data?.subscription_plan || "trial",
          blockedModules: subRes.data?.blocked_modules || [],
          trialDaysLeft: subRes.data?.trial_days_left,
          allowVideo: subRes.data?.allow_video || false,
        });
      }

      setModules(modulesRes.data?.modules || []);
    } catch {
      // Keep defaults
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (user) fetchAll();
  }, [user, fetchAll]);

  // Re-check every 5 minutes so subscription changes (payment success/failure,
  // trial expiry) are reflected without requiring a page reload.
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(fetchAll, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user, fetchAll]);

  const isBlocked = useCallback((moduleOrPath) => {
    // Trial has full access
    if (state.status === "trial" && state.trialDaysLeft > 0) return false;
    if (state.blockedModules.includes("all")) return true;
    
    const module = ROUTE_TO_MODULE[moduleOrPath] || moduleOrPath;
    
    // Check if blocked by base plan
    if (state.blockedModules.includes(module)) {
      // Check if has marketplace subscription for this module
      const marketplaceModule = modules.find(m => m.id === module);
      if (marketplaceModule?.is_active) return false;
      return true;
    }
    
    return false;
  }, [state, modules]);

  const hasModuleAccess = useCallback((moduleId) => {
    // Trial has full access
    if (state.status === "trial" && state.trialDaysLeft > 0) return true;
    
    const module = modules.find(m => m.id === moduleId);
    return module?.is_active ?? true;
  }, [state, modules]);

  const getModuleInfo = useCallback((moduleId) => {
    return modules.find(m => m.id === moduleId);
  }, [modules]);

  return (
    <SubscriptionContext.Provider value={{
      ...state,
      isBlocked,
      allowVideo: state.status === "trial" ? true : state.allowVideo,
      isSuspended: state.status === "suspended",
      modules,
      loading,
      hasModuleAccess,
      getModuleInfo,
      refetch: fetchAll,
    }}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => useContext(SubscriptionContext);
